import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { PAGE_GROUPS, isStorefrontHref, loadStorefrontPages, searchPages } from "./storefrontPages";

/**
 * What the link button opens: the store's own pages to pick from, with a
 * search box, and a line for another address on nysmah.com. Writers never
 * type a page's address; the one they pick is inserted in `lang`.
 */
export default function PagePicker({ lang, currentPath, onPick, onClose }) {
  const { t } = useTranslation();
  const [pages, setPages] = useState(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [custom, setCustom] = useState("");
  const [customError, setCustomError] = useState(false);

  useEffect(() => {
    let alive = true;
    loadStorefrontPages(lang)
      .then((list) => alive && setPages(list))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [lang]);

  const shown = useMemo(() => searchPages(pages || [], query, currentPath), [pages, query, currentPath]);

  const pickCustom = () => {
    const href = custom.trim();
    if (!isStorefrontHref(href)) {
      setCustomError(true);
      return;
    }
    onPick({ href, text: href });
  };

  return (
    <Modal show onHide={onClose} centered className="lp-picker">
      <Modal.Header closeButton>
        <Modal.Title as="h2" className="lp-picker__title">
          {t("Link to a page")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="lp-picker__hint">
          {t("The words you selected become the link. With nothing selected, the page's name is inserted.")}
        </p>
        <label className="lp-picker__search">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            className="nb-input"
            placeholder={t("Search our pages")}
            aria-label={t("Search our pages")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </label>

        {failed ? (
          <p className="lp-picker__empty">{t("The page list could not be read. Try again in a moment.")}</p>
        ) : pages === null ? (
          <p className="lp-picker__empty">{t("Loading pages…")}</p>
        ) : shown.length === 0 ? (
          <p className="lp-picker__empty">{t("No page matches that.")}</p>
        ) : (
          PAGE_GROUPS.map(([type, title]) => {
            const group = shown.filter((page) => page.type === type);
            if (!group.length) return null;
            return (
              <div key={type} className="lp-picker__group">
                <p className="lp-picker__group-title">{t(title)}</p>
                <ul className="lp-picker__list">
                  {group.map((page) => (
                    <li key={page.path}>
                      <button
                        type="button"
                        className="lp-picker__item"
                        onClick={() => onPick({ href: `/${lang}/${page.path}`, text: page.name })}
                      >
                        <span className="lp-picker__name">{page.name}</span>
                        <span className="lp-picker__path">/{page.path}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}

        <div className="lp-picker__custom">
          <input
            type="text"
            className={`nb-input${customError ? " is-invalid" : ""}`}
            placeholder="/en/… or https://nysmah.com/…"
            aria-label={t("Another address on nysmah.com")}
            dir="ltr"
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              setCustomError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                pickCustom();
              }
            }}
          />
          <button type="button" className="nb-btn nb-btn--ghost" onClick={pickCustom}>
            {t("Insert")}
          </button>
        </div>
        {customError && (
          <p className="nb-fielderror" role="alert">
            {t("Only addresses on nysmah.com can be linked, starting with / or https://nysmah.com/.")}
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
}
